import { useCallback, useRef } from 'react';
import { Keyboard, Platform, type NativeScrollEvent, type NativeSyntheticEvent } from 'react-native';

const DEFAULT_SCROLL_THRESHOLD = 8;

export type DismissKeyboardOnScrollMode = 'swipeDown' | 'anyDirection';

interface IUseDismissKeyboardOnScrollOptions {
  /**
   * `swipeDown` dismisses when the user drags downward through the list
   * (revealing older content above, like reading back through a chat).
   * `anyDirection` dismisses as soon as the user begins dragging the list.
   * @default 'swipeDown'
   */
  mode?: DismissKeyboardOnScrollMode;
  /** Minimum downward drag distance (in px) before dismissing. @default 8 */
  threshold?: number;
  /** @default true */
  enabled?: boolean;
}

/**
 * Dismisses the keyboard in response to list scroll gestures.
 * In `swipeDown` mode, scroll events are only evaluated while the user is
 * actively dragging the list. Drag begin/end events are the only scroll events
 * Android emits exclusively for touch gestures — layout-driven scroll events
 * (e.g. the viewport shrinking when the composer grows a line) and programmatic
 * scrolls (which emit momentum begin/end when animated, such as the list
 * auto-scrolling to keep the bottom pinned) must not dismiss.
 */
export function useDismissKeyboardOnScroll(options: IUseDismissKeyboardOnScrollOptions = {}) {
  const {
    mode = 'swipeDown',
    threshold = DEFAULT_SCROLL_THRESHOLD,
    enabled = true,
  } = options;

  const scrollDismissStateRef = useRef({ dismissed: false, distanceFromBottomAtStart: 0 });
  const isDraggingRef = useRef(false);

  const dismissKeyboard = useCallback(() => {
    if (!enabled || Platform.OS === 'web') return;
    Keyboard.dismiss();
  }, [enabled]);

  const onScrollBeginDrag = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (!enabled || Platform.OS === 'web') return;

      if (mode === 'anyDirection') {
        dismissKeyboard();
        return;
      }

      isDraggingRef.current = true;
      const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
      scrollDismissStateRef.current = {
        dismissed: false,
        distanceFromBottomAtStart:
          contentSize.height - layoutMeasurement.height - contentOffset.y,
      };
    },
    [dismissKeyboard, enabled, mode]
  );

  const onScrollEndDrag = useCallback(() => {
    isDraggingRef.current = false;
  }, []);

  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (!enabled || Platform.OS === 'web' || mode !== 'swipeDown') return;
      if (!isDraggingRef.current) return;

      const state = scrollDismissStateRef.current;
      if (state.dismissed) return;

      const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
      const distanceFromBottom =
        contentSize.height - layoutMeasurement.height - contentOffset.y;

      if (distanceFromBottom > state.distanceFromBottomAtStart + threshold) {
        state.dismissed = true;
        dismissKeyboard();
      }
    },
    [dismissKeyboard, enabled, mode, threshold]
  );

  return {
    onScrollBeginDrag,
    onScrollEndDrag,
    onScroll,
  };
}
